import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { X, Search, ArrowRightLeft, ArrowDownLeft, ArrowUpRight, Receipt, Send } from 'lucide-react';
import { useEntity } from '../../contexts/EntityContext';
import { useToast } from '../ui/Toast';
import { api, Wallet } from '../../lib/api';
import { isCashierEntityAllowed } from './constants';

interface NewTransactionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

type TransactionType = 'exchange' | 'deposit' | 'withdrawal' | 'expense' | 'transfer';

interface WalletOption {
  id: string;
  name: string;
  currency: string;
}

// Zod schema for form validation
const transactionFormSchema = z.object({
  type: z.enum(['exchange', 'deposit', 'withdrawal', 'expense', 'transfer']),
  clientId: z.string().min(1, 'Client is required'),
  // For exchange
  amountIn: z.string().optional(),
  walletInId: z.string().optional(),
  amountOut: z.string().optional(),
  walletOutId: z.string().optional(),
  exchangeRate: z.string().optional(),
  // For single currency transactions
  amount: z.string().optional(),
  walletId: z.string().optional(),
  // For transfer
  walletFromId: z.string().optional(),
  walletToId: z.string().optional(),
  // Common
  comment: z.string().optional(),
  category: z.string().optional(),
});

type TransactionFormData = z.infer<typeof transactionFormSchema>;



const CRYPTO_CURRENCIES = ['USDT', 'BTC', 'ETH', 'USDC', 'BNB', 'SOL', 'XRP'];
const isCryptoCurrency = (code: string) => CRYPTO_CURRENCIES.includes(code.toUpperCase());

export function NewTransactionModal({ isOpen, onClose, onSuccess }: NewTransactionModalProps) {
  const { currentEntity } = useEntity();
  const toast = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [wallets, setWallets] = useState<Wallet[]>([]);
  const [walletsLoading, setWalletsLoading] = useState(true);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm<TransactionFormData>({
    resolver: zodResolver(transactionFormSchema),
    defaultValues: {
      type: 'exchange',
      clientId: '',
      amountIn: '',
      amountOut: '',
      walletInId: '',
      walletOutId: '',
      exchangeRate: '',
      amount: '',
      walletId: '',
      walletFromId: '',
      walletToId: '',
      comment: '',
      category: '',
    },
  });

  const transactionType = watch('type');
  const amountIn = watch('amountIn');
  const amountOut = watch('amountOut');
  const exchangeRate = watch('exchangeRate');
  const walletInId = watch('walletInId');
  const walletOutId = watch('walletOutId');
  const walletFromId = watch('walletFromId');

  // Fetch wallets
  useEffect(() => {
    async function fetchWallets() {
      if (!currentEntity?.id || !isOpen) return;

      // Only fetch wallets for FNK entity
      if (!isCashierEntityAllowed(currentEntity.code)) {
        setWallets([]);
        setWalletsLoading(false);
        return;
      }

      try {
        setWalletsLoading(true);
        const result = await api.admin.wallets.list(currentEntity.id);
        setWallets(result.data.filter(w => w.isActive));
      } catch (err) {
        console.error('Failed to load wallets:', err);
        toast.error('Failed to load wallets');
      } finally {
        setWalletsLoading(false);
      }
    }
    fetchWallets();
  }, [currentEntity?.id, currentEntity?.code, isOpen, toast]);

  // Calculate exchange rate or amounts
  useEffect(() => {
    if (transactionType !== 'exchange') return;

    const inVal = parseFloat(amountIn || '0');
    const outVal = parseFloat(amountOut || '0');
    const rate = parseFloat(exchangeRate || '0');

    if (inVal > 0 && rate > 0 && !outVal) {
      setValue('amountOut', (inVal * rate).toFixed(2));
    }
  }, [amountIn, exchangeRate, amountOut, transactionType, setValue]);

  const fiatWallets: WalletOption[] = wallets
    .filter(w => !isCryptoCurrency(w.currencyCode))
    .map(w => ({ id: w.id, name: w.name, currency: w.currencyCode }));

  const cryptoWallets: WalletOption[] = wallets
    .filter(w => isCryptoCurrency(w.currencyCode))
    .map(w => ({ id: w.id, name: w.name, currency: w.currencyCode }));

  const allWallets: WalletOption[] = wallets.map(w => ({
    id: w.id,
    name: w.name,
    currency: w.currencyCode,
  }));

  const getSelectedCurrency = (walletId: string) => {
    const wallet = allWallets.find(w => w.id === walletId);
    return wallet?.currency || '';
  };

  const selectedFromCurrency = getSelectedCurrency(walletFromId || '');
  const transferToOptions = allWallets.filter(
    w => w.id !== walletFromId && (!walletFromId || w.currency === selectedFromCurrency)
  );

  const onFormSubmit = async (data: TransactionFormData) => {
    if (!currentEntity?.id) {
      toast.error('No entity selected');
      return;
    }

    setIsSubmitting(true);

    try {
      let payload: any = {
        type: data.type,
        status: 'closed',
        clientId: data.clientId,
        comment: data.comment,
      };

      if (data.type === 'exchange') {
        const currencyIn = getSelectedCurrency(data.walletInId || '');
        const currencyOut = getSelectedCurrency(data.walletOutId || '');
        payload = {
          ...payload,
          amountIn: data.amountIn,
          currencyIn,
          walletInId: data.walletInId,
          amountOut: data.amountOut,
          currencyOut,
          walletOutId: data.walletOutId,
          exchangeRate: data.exchangeRate,
        };
      } else if (data.type === 'transfer') {
        const currency = getSelectedCurrency(data.walletFromId || '');
        if (!currency) {
          toast.error('Please select valid wallets for transfer');
          setIsSubmitting(false);
          return;
        }
        payload = {
          ...payload,
          amount: data.amount,
          currency,
          walletFromId: data.walletFromId,
          walletToId: data.walletToId,
        };
      } else {
        const currency = getSelectedCurrency(data.walletId || '');
        payload = {
          ...payload,
          amount: data.amount,
          currency,
          walletId: data.walletId,
        };
        if (data.type === 'expense') {
          payload.category = data.category;
        }
      }

      await api.transactions.create(currentEntity.id, payload);
      toast.success('Transaction created successfully');
      reset();
      onSuccess();
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to create transaction');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  if (!isOpen) return null;

  const typeIcons: Record<TransactionType, React.ReactNode> = {
    exchange: <ArrowRightLeft size={18} />,
    deposit: <ArrowDownLeft size={18} />,
    withdrawal: <ArrowUpRight size={18} />,
    expense: <Receipt size={18} />,
    transfer: <Send size={18} />,
  };

  return createPortal(
    <>
      <div className="modal-overlay" onClick={handleClose} />
      <div className="modal-container">
        <div className="modal-header">
          <h2 className="modal-title">New Transaction</h2>
          <button className="modal-close" onClick={handleClose}>
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit(onFormSubmit)} className="modal-form">
          {/* Transaction Type Selector */}
          <div className="type-selector">
            {(['exchange', 'deposit', 'withdrawal', 'expense', 'transfer'] as TransactionType[]).map((type) => (
              <button
                key={type}
                type="button"
                className={`type-btn ${transactionType === type ? 'active' : ''}`}
                onClick={() => setValue('type', type)}
              >
                {typeIcons[type]}
                <span>{type.charAt(0).toUpperCase() + type.slice(1)}</span>
              </button>
            ))}
          </div>

          {/* Client Search */}
          <div className="form-group">
            <label className="form-label">Client</label>
            <div className="input-with-icon">
              <Search className="input-icon" size={18} />
              <input
                type="text"
                className={`form-input ${errors.clientId ? 'error' : ''}`}
                placeholder="Search or enter client name..."
                {...register('clientId')}
              />
            </div>
            {errors.clientId && <span className="field-error">{errors.clientId.message}</span>}
          </div>

          {/* Exchange Type Fields */}
          {transactionType === 'exchange' && (
            <>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Received Amount</label>
                  <div className="amount-input-group">
                    <input
                      type="text"
                      className="form-input amount-input"
                      placeholder="0.00"
                      {...register('amountIn')}
                    />
                    <select
                      className="currency-select"
                      {...register('walletInId')}
                      disabled={walletsLoading}
                    >
                      <option value="">Wallet</option>
                      {cryptoWallets.map(w => (
                        <option key={w.id} value={w.id}>{w.currency} - {w.name}</option>
                      ))}
                      {fiatWallets.map(w => (
                        <option key={w.id} value={w.id}>{w.currency} - {w.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Given Amount</label>
                  <div className="amount-input-group">
                    <input
                      type="text"
                      className="form-input amount-input"
                      placeholder="0.00"
                      {...register('amountOut')}
                    />
                    <select
                      className="currency-select"
                      {...register('walletOutId')}
                      disabled={walletsLoading}
                    >
                      <option value="">Wallet</option>
                      {fiatWallets.map(w => (
                        <option key={w.id} value={w.id}>{w.currency} - {w.name}</option>
                      ))}
                      {cryptoWallets.map(w => (
                        <option key={w.id} value={w.id}>{w.currency} - {w.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Exchange Rate</label>
                <div className="rate-input-wrapper">
                  <input
                    type="text"
                    className="form-input"
                    placeholder="0.0000"
                    {...register('exchangeRate')}
                  />
                  <span className="rate-pair">
                    {getSelectedCurrency(walletInId || '') || '???'}/{getSelectedCurrency(walletOutId || '') || '???'}
                  </span>
                </div>
              </div>
            </>
          )}

          {/* Transfer Fields */}
          {transactionType === 'transfer' && (
            <>
              <div className="form-group">
                <label className="form-label">Transfer Amount</label>
                <div className="amount-input-group">
                  <input
                    type="text"
                    className="form-input amount-input"
                    placeholder="0.00"
                    {...register('amount')}
                  />
                  <span className="rate-pair" style={{ borderLeft: '1px solid var(--color-border)' }}>
                    {selectedFromCurrency || '???'}
                  </span>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">From Wallet</label>
                  <select
                    className="form-input"
                    {...register('walletFromId', {
                      onChange: () => {
                        setValue('walletToId', ''); // Reset TO wallet when FROM changes
                      }
                    })}
                    disabled={walletsLoading}
                  >
                    <option value="">Select From Wallet</option>
                    {allWallets.map(w => (
                      <option key={w.id} value={w.id}>{w.currency} - {w.name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">To Wallet</label>
                  <select
                    className="form-input"
                    {...register('walletToId')}
                    disabled={walletsLoading || !walletFromId}
                  >
                    <option value="">Select To Wallet</option>
                    {transferToOptions.map(w => (
                      <option key={w.id} value={w.id}>{w.name}</option>
                    ))}
                  </select>
                </div>
              </div>
            </>
          )}

          {/* Single Amount Fields (deposit, withdrawal, expense) */}
          {transactionType !== 'exchange' && transactionType !== 'transfer' && (
            <>
              <div className="form-group">
                <label className="form-label">Amount</label>
                <div className="amount-input-group">
                  <input
                    type="text"
                    className="form-input amount-input"
                    placeholder="0.00"
                    {...register('amount')}
                  />
                  <select
                    className="currency-select"
                    {...register('walletId')}
                    disabled={walletsLoading}
                  >
                    <option value="">Select Wallet</option>
                    {allWallets.map(w => (
                      <option key={w.id} value={w.id}>{w.currency} - {w.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              {transactionType === 'expense' && (
                <div className="form-group">
                  <label className="form-label">Category</label>
                  <select className="form-input" {...register('category')}>
                    <option value="">Select category...</option>
                    <option value="rent">Rent</option>
                    <option value="utilities">Utilities</option>
                    <option value="supplies">Supplies</option>
                    <option value="salary">Salary</option>
                    <option value="commission">Commission</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              )}
            </>
          )}

          {/* Comment */}
          <div className="form-group">
            <label className="form-label">Description / Comment</label>
            <textarea
              className="form-input form-textarea"
              placeholder="Add notes about this transaction..."
              rows={2}
              {...register('comment')}
            />
          </div>

          {/* Actions */}
          <div className="modal-actions">
            <button type="button" className="btn-cancel" onClick={handleClose}>
              Cancel
            </button>
            <button type="submit" className="btn-submit" disabled={isSubmitting || walletsLoading}>
              {isSubmitting ? 'Creating...' : 'Create Transaction'}
            </button>
          </div>
        </form>
      </div>

      <style>{`
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.7);
          backdrop-filter: blur(2px);
          z-index: 9999;
          animation: fadeIn 0.15s ease-out;
        }

        .modal-container {
          position: fixed;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          background: var(--color-surface);
          border-radius: 12px;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
          width: 90%;
          max-width: 560px;
          max-height: 90vh;
          overflow-y: auto;
          z-index: 10000;
          animation: slideIn 0.2s ease-out;
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translate(-50%, -48%);
          }
          to {
            opacity: 1;
            transform: translate(-50%, -50%);
          }
        }

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px 24px;
          border-bottom: 1px solid var(--color-border);
        }

        .modal-title {
          font-size: 20px;
          font-weight: 600;
          color: var(--color-text);
          margin: 0;
        }

        .modal-close {
          width: 36px;
          height: 36px;
          border: none;
          background: transparent;
          color: var(--color-text-muted);
          cursor: pointer;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.15s;
        }

        .modal-close:hover {
          background: var(--color-surface-hover);
          color: var(--color-text);
        }

        .modal-form {
          padding: 24px;
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        /* Type Selector */
        .type-selector {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
        }

        .type-btn {
          flex: 1;
          min-width: 90px;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 6px;
          padding: 12px 8px;
          border: 1px solid var(--color-border);
          border-radius: 8px;
          background: var(--color-bg);
          color: var(--color-text-muted);
          cursor: pointer;
          transition: all 0.15s;
          font-size: 11px;
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 0.3px;
        }

        .type-btn:hover {
          border-color: var(--ctp-teal);
          color: var(--ctp-teal);
        }

        .type-btn.active {
          background: rgba(13, 148, 136, 0.1);
          border-color: var(--ctp-teal);
          color: var(--ctp-teal);
        }

        /* Form Elements */
        .form-group {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
        }

        .form-label {
          font-size: 12px;
          font-weight: 600;
          color: var(--color-text-muted);
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .form-input {
          width: 100%;
          padding: 12px 14px;
          border: 1px solid var(--color-border);
          border-radius: 8px;
          font-size: 14px;
          color: var(--color-text);
          background: var(--color-bg);
          transition: all 0.15s;
        }

        .form-input:focus {
          outline: none;
          border-color: var(--ctp-teal);
          box-shadow: 0 0 0 3px rgba(13, 148, 136, 0.1);
        }

        .form-input::placeholder {
          color: var(--color-text-muted);
        }

        .form-input.error {
          border-color: var(--ctp-red);
        }

        .form-textarea {
          resize: vertical;
          min-height: 60px;
        }

        .input-with-icon {
          position: relative;
        }

        .input-icon {
          position: absolute;
          left: 14px;
          top: 50%;
          transform: translateY(-50%);
          color: var(--color-text-muted);
        }

        .input-with-icon .form-input {
          padding-left: 42px;
        }

        /* Amount Input Group */
        .amount-input-group {
          display: flex;
          border: 1px solid var(--color-border);
          border-radius: 8px;
          overflow: hidden;
        }

        .amount-input-group .form-input {
          border: none;
          border-radius: 0;
          flex: 1;
        }

        .amount-input-group .form-input:focus {
          box-shadow: none;
        }

        .amount-input {
          font-family: 'SF Mono', 'Consolas', monospace;
          font-weight: 600;
        }

        .currency-select {
          padding: 12px 36px 12px 12px;
          min-width: 140px;
          background: var(--color-surface);
          border: 1px solid var(--color-border);
          border-radius: 8px;
          font-size: 13px;
          font-weight: 500;
          color: var(--color-text);
          cursor: pointer;
          appearance: none;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E");
          background-repeat: no-repeat;
          background-position: right 12px center;
        }

        .currency-select:focus {
          outline: none;
          border-color: var(--ctp-teal);
        }

        .currency-select:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        /* Rate Input */
        .rate-input-wrapper {
          display: flex;
          align-items: center;
          border: 1px solid var(--color-border);
          border-radius: 8px;
          overflow: hidden;
        }

        .rate-input-wrapper .form-input {
          border: none;
          border-radius: 0;
          font-family: 'SF Mono', 'Consolas', monospace;
          font-weight: 600;
        }

        .rate-input-wrapper .form-input:focus {
          box-shadow: none;
        }

        .rate-pair {
          padding: 12px 16px;
          background: var(--color-surface-hover);
          color: var(--color-text-muted);
          font-size: 12px;
          font-weight: 500;
          white-space: nowrap;
        }

        /* Field Error */
        .field-error {
          font-size: 12px;
          color: var(--ctp-red);
        }

        /* Modal Actions */
        .modal-actions {
          display: flex;
          gap: 12px;
          justify-content: flex-end;
          padding-top: 8px;
          border-top: 1px solid var(--color-border);
          margin-top: 4px;
        }

        .btn-cancel {
          padding: 12px 24px;
          border: 1px solid var(--color-border);
          border-radius: 8px;
          background: transparent;
          color: var(--color-text);
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.15s;
        }

        .btn-cancel:hover {
          background: var(--color-surface-hover);
        }

        .btn-submit {
          padding: 12px 24px;
          border: none;
          border-radius: 8px;
          background: var(--ctp-teal);
          color: white;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.15s;
        }

        .btn-submit:hover:not(:disabled) {
          background: #0f766e;
        }

        .btn-submit:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        /* Responsive */
        @media (max-width: 600px) {
          .modal-container {
            width: 95%;
            max-height: 95vh;
          }

          .type-selector {
            flex-wrap: wrap;
          }

          .type-btn {
            min-width: calc(33.33% - 6px);
          }

          .form-row {
            grid-template-columns: 1fr;
          }

          .modal-actions {
            flex-direction: column;
          }

          .btn-cancel,
          .btn-submit {
            width: 100%;
          }
        }
      `}</style>
    </>,
    document.body
  );
}
